<template>
	<section
		class="page page-extras-pad"
		v-bind:class="{
			'has-scrolled': hasScrolled,
		}"
	>
		<ChalkyUiPageHero
			description="Diagram pool layouts so you can try them again later."
			title="Pad"
			v-bind:imageUrl="$asset('image/graphic/hero-pad.jpg')"
		/>

		<section class="full-panel" v-if="$store.getters['authentication/authenticated'] == false">
			<ViewUiCalloutIcon
				class="view-venue-nodiagrams"
				iconFilename="rack-9ball.svg"
				v-bind:message="'You must login to <br />use Pad features'"
			/>
		</section>

		<ChalkyUtilityTabGroup
			v-bind:tabs="availableTabs"
			v-bind:useUrl="true"
			v-else
			v-on:tab-change="Handle_OnTabChange"
		>
			<template v-slot="{ activeTab }">
				<div v-if="activeTab === 'my-diagrams'">
					<!-- <ViewExtrasPadGallery class="list" v-bind:diagramCollection="diagramCollection" /> -->

					<section class="full-panel">
						<ViewUiCalloutIcon
							class="view-venue-nodiagrams"
							iconFilename="rack-9ball.svg"
							v-bind:message="'Your saved gallery<br />is coming soon'"
						/>
					</section>
				</div>

				<div v-if="activeTab === 'gallery'">
					<!-- <ViewExtrasPadGallery class="list" v-bind:diagramCollection="diagramCollection" /> -->

					<section class="full-panel">
						<ViewUiCalloutIcon
							class="view-venue-nodiagrams"
							iconFilename="rack-9ball.svg"
							v-bind:message="'The public gallery<br />is coming soon'"
						/>
					</section>
				</div>

				<div v-if="activeTab === 'create'">
					<ViewPadCreate v-bind:diagramModel="diagramModel" />
				</div>
			</template>
		</ChalkyUtilityTabGroup>
	</section>
</template>

<script lang="ts">
	import * as Mixins from '@/Mixins';
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component, Prop, Watch } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * mk: We are manually setting query params in here which we shouldn't be doing
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component({
		mixins: [Mixins.StickyScroll],
	})
	export default class PageExtrasPad extends PageBase {
		/**
		 * @type string[]
		 */
		protected get availableTabs(): string[] {
			// return ['Create'];
			// return ['Create', 'My Diagrams'];
			return ['Create', 'Gallery', 'My Diagrams'];
		}

		/**
		 * For fetching gallery or my own
		 *
		 * @type ChalkySticks/Collection/Diagram
		 */
		public diagramCollection: ChalkySticks.Collection.Diagram = ChalkySticks.Factory.Diagram.collection({
			limit: 24,
		});

		/**
		 * For having control over the Pad and Trainer
		 *
		 * @type ChalkySticks/Model/Diagram
		 */
		public diagramModel: ChalkySticks.Model.Diagram = ChalkySticks.Factory.Diagram.model();

		/**
		 * Current active tab slug
		 *
		 * @type string
		 */
		protected currentActiveTab: string = '';

		/**
		 * Configuration for sticky behavior
		 * @type IStickyScrollConfig
		 */
		protected stickyConfig: any = {
			scrollThreshold: 100,
		};

		/**
		 * @return Promise<void>
		 */
		@mounted
		protected async setup(): Promise<void> {
			this.currentActiveTab = this.$route.query.tab?.toString() || 'gallery';

			switch (this.$route.query.tab) {
				case 'create':
					this.fetchDiagram();
					break;

				case 'gallery':
				default:
					this.fetchGallery();
					break;

				case 'my-diagrams':
					this.fetchMine();
					break;
			}
		}

		/**
		 * @return void
		 */
		protected async fetchDiagram(): Promise<void> {
			const diagramId = this.$route.query.pad?.toString() || '';

			// Nothing to rack
			if (!diagramId) {
				return;
			}

			// Set and fetch
			this.diagramModel.setId(diagramId);
			await this.diagramModel.fetch();

			ChalkySticks.Utility.Log.debug('pad', 'Fetched diagram', this.diagramModel);
		}

		/**
		 * @return void
		 */
		protected fetchGallery(): void {
			this.diagramCollection.unsetQueryParam('user_id');
			this.diagramCollection.fetch();
		}

		/**
		 * @return void
		 */
		protected fetchMine(): void {
			const userModel = this.$store.getters['authentication/user'] as ChalkySticks.Model.User;
			const userId = userModel?.id || 0;

			if (userId) {
				this.diagramCollection.setQueryParam('user_id', userId.toString()).fetch();
			}
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnTabChange(slug: string): Promise<void> {
			// Update current active tab
			this.currentActiveTab = slug;

			switch (slug) {
				case 'create':
					await this.fetchDiagram();
					break;

				case 'gallery':
				default:
					this.fetchGallery();
					break;

				case 'my-diagrams':
					await this.fetchMine();
					break;
			}

			if (slug === 'gallery' && this.$route.query.pad) {
				// Remove pad from query params
				this.$router.replace({
					name: 'PageExtrasPad',
					query: {
						...this.$route.query,
						pad: undefined,
						tab: slug,
					},
				});
			}
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.page-extras-pad {
		overflow: hidden !important;
	}

	// Media Queries
	// ---------------------------------------------------------------------------

	@include media-query('mobile-landscape') {
		#app[page='PageExtrasPad'] {
			#GlobalHeader {
				opacity: 0;
				pointer-events: none;
				transition: opacity 0.5s ease-in-out;
			}

			.ui-pagehero {
				display: none;
			}
		}
	}
</style>
